(() => {
  // Shopify can re-inject this file during editor reloads. Keep one global runtime.
  if (window.__abJournalArticleRuntime) {
    window.__abJournalArticleRuntime.initializeAll();
    return;
  }
  const activeRoots = new Set();
  function init(root) {
    if (root.dataset.abInitialized === 'true') return;
    root.dataset.abInitialized = 'true';
    const controller = new AbortController();
    const signal = controller.signal;
    const listen = (target, event, handler, options = {}) => {
      if (target) target.addEventListener(event, handler, { ...options, signal });
    };
    activeRoots.add(root);
    root.__abJournalArticleCleanup = () => {
      controller.abort();
      activeRoots.delete(root);
      delete root.__abJournalArticleCleanup;
      delete root.dataset.abInitialized;
    };
    const body = root.querySelector('[data-ab-article-body]');

    // Normalize legacy image wrappers only when they contain no editorial prose.
    // In particular, older articles often float large image containers beside
    // paragraphs, collapsing text to a narrow sliver.
    if (body) {
      body.querySelectorAll('img').forEach(img => {
        let media = img.closest('figure, p, div');
        if (!media || media === body) return;
        const text = (media.textContent || '').trim();
        if (text.length > 80 || media.querySelectorAll('img').length > 1) return;
        const inlineFloat = /float\s*:\s*(left|right)/i;
        if (inlineFloat.test(media.getAttribute('style') || '') || ['left','right'].includes(media.getAttribute('align'))) {
          media.style.cssText += ';float:none!important;clear:both;max-width:100%;width:auto;';
        }
      });
    }
    // Normalise empty legacy spacing and duplicated body H1s without editing the
    // underlying Shopify article. Keep media, embedded content and links intact.
    if (body) {
      body.querySelectorAll('p').forEach(p => {
        if (p.querySelector('img,picture,video,iframe,svg,table,a,button,input,hr')) return;
        if (!(p.textContent || '').replace(/[\\u00a0\\u200b\\u200c\\u200d]/g, '').trim()) {
          p.hidden = true;
        }
      });
      body.querySelectorAll('h1').forEach(h1 => {
        const h2 = document.createElement('h2');
        [...h1.attributes].forEach(attr => h2.setAttribute(attr.name, attr.value));
        while (h1.firstChild) h2.appendChild(h1.firstChild);
        h1.replaceWith(h2);
      });
    }
    // Hide only a clearly-labelled, terminal reading-list block. Do not remove
    // arbitrary related text, article citations or editorial in-line links.
    if (body && root.dataset.abHideLegacyRelated === 'true' && root.querySelector('.ab-journal-article__story-card')) {
      const normalize = value => (value || '').replace(/\s+/g, ' ').replace(/[:.!–—-]+$/g, '').trim().toLowerCase();
      const labels = /^(you (might|may|could) also (enjoy|like|love|read)( reading)?|related (articles|stories|posts|reading)|read (more|next)|further reading|continue reading|more from (the )?(blog|journal))$/;
      const children = Array.from(body.children);
      for (let i = children.length - 1; i >= Math.max(0, children.length - 12); i--) {
        const heading = children[i];
        if (!/^(P|H[2-6]|DIV)$/.test(heading.tagName) || !labels.test(normalize(heading.textContent))) continue;
        const tail = children.slice(i + 1).filter(el => normalize(el.textContent));
        if (!tail.length || tail.length > 4) continue;
        const hasMedia = tail.some(el => el.querySelector('img,iframe,video'));
        const linkCount = tail.reduce((n, el) => n + el.querySelectorAll('a[href*="/blogs/"]').length, 0);
        const totalWords = tail.map(el => el.textContent).join(' ').trim().split(/\s+/).length;
        const onlyLists = tail.every(el => /^(OL|UL|P|DIV)$/.test(el.tagName));
        if (hasMedia || !onlyLists || !linkCount || totalWords > 100) continue;
        [heading, ...tail].forEach(el => { el.hidden = true; el.setAttribute('data-ab-hidden-related', ''); });
        break;
      }
    }

    // Move suitable standalone article images into a gallery, rather than
    // cloning them. Moving preserves links, caption text and alt attributes.
    const rail = root.querySelector('[data-ab-media-rail]');
    if (body && rail) {
      // Gather standalone media throughout the article, not just the opening.
      // Legacy articles commonly insert another image after several paragraphs.
      // Only move self-contained image blocks: retain prose, tables, embeds,
      // illustration-with-copy layouts and editorial links in the reading flow.
      const children = Array.from(body.children);
      const candidate = el => {
        if (el.hidden || el.hasAttribute('data-ab-hidden-related')) return false;
        if (el.matches('table,script,style') || el.querySelector('table,iframe,video,form')) return false;
        const imgs = el.querySelectorAll('img');
        if (imgs.length !== 1) return false;
        if (imgs[0].closest('table')) return false;
        if (el.querySelector('h1,h2,h3,h4,h5,h6')) return false;
        const caption = el.querySelector('figcaption');
        const captionLength = caption ? caption.textContent.trim().length : 0;
        const otherText = Math.max(0,(el.textContent || '').trim().length - captionLength);
        return otherText <= 90;
      };
      const galleryCandidates = children.filter(candidate).slice(0, 80);
      if (galleryCandidates.length) {
        const stage = rail.querySelector('[data-ab-media-stage]');
        const count = rail.querySelector('[data-ab-media-count]');
        const toolbar = rail.querySelector('[data-ab-media-toolbar]');
        const dots = rail.querySelector('[data-ab-gallery-dots]');
        let selected = 0;
        const slides = galleryCandidates.map((node, i) => {
          const slide = document.createElement('div');
          slide.className = 'ab-journal-article__media-slide';
          slide.setAttribute('role', 'group');
          slide.setAttribute('aria-label', 'Image ' + (i + 1) + ' of ' + galleryCandidates.length);
          // Preserve image and caption, and clean up float styles from Shopify RTE.
          node.style.cssText += ';float:none!important;clear:both;max-width:100%!important;width:auto!important';
          node.querySelectorAll('img').forEach(img => {
            img.style.cssText += ';float:none!important;max-width:100%!important;height:auto!important';
            if (!(img.getAttribute('alt') || '').trim()) {
              const storyTitle = root.querySelector('.ab-journal-article__title')?.textContent?.trim() || 'this Journal article';
              img.setAttribute('alt', 'Image ' + (i + 1) + ' from ' + storyTitle);
            }
          });
          slide.appendChild(node);
          stage.appendChild(slide);
          if (galleryCandidates.length > 1) {
            const dot = document.createElement('button');
            dot.type = 'button';
            dot.setAttribute('aria-label', 'Show image ' + (i + 1) + ' of ' + galleryCandidates.length);
            listen(dot, 'click', () => show(i));
            dots.appendChild(dot);
          }
          return slide;
        });
        const show = i => {
          selected = (i + slides.length) % slides.length;
          slides.forEach((slide, index) => {
            const active = index === selected;
            slide.classList.toggle('is-active', active);
            slide.hidden = !active;
          });
          const firstDot = Math.max(0, Math.min(selected - 4, slides.length - 9));
          dots.querySelectorAll('button').forEach((dot, index) => {
            dot.setAttribute('aria-current', String(index === selected));
            dot.hidden = slides.length > 9 && (index < firstDot || index >= firstDot + 9);
          });
          if (count) count.textContent = (selected + 1) + ' / ' + slides.length;
        };
        if (slides.length > 1) {
          if (toolbar) toolbar.hidden = false;
          listen(rail.querySelector('[data-ab-gallery-prev]'), 'click', () => show(selected - 1));
          listen(rail.querySelector('[data-ab-gallery-next]'), 'click', () => show(selected + 1));
        }
        // Float the entire gallery inside the same rich-text flow. This lets
        // lines wrap beneath its *actual* height, even midway through a paragraph.
        // The gallery remains the first visual element; later article images
        // still participate in its existing slideshow.
        rail.hidden = false;
        body.insertBefore(rail, body.firstChild);
        root.classList.add('ab-journal-article--flow-gallery');
        show(0);
        const openZoom = root.querySelector('[data-ab-zoom-open]');
        const dialog = root.querySelector('[data-ab-zoom-dialog]');
        const zoomImg = root.querySelector('[data-ab-zoom-image]');
        const zoomCaption = root.querySelector('[data-ab-zoom-caption]');
        if (openZoom && dialog && zoomImg) {
          listen(openZoom, 'click', () => {
            const img = slides[selected]?.querySelector('img');
            if (!img) return;
            zoomImg.src = img.currentSrc || img.src;
            zoomImg.alt = img.alt || 'Article photograph';
            if (zoomCaption) zoomCaption.textContent = slides[selected].querySelector('figcaption')?.textContent.trim() || '';
            if (typeof dialog.showModal === 'function') dialog.showModal();
          });
          listen(root.querySelector('[data-ab-zoom-close]'), 'click', () => dialog.close());
          listen(dialog, 'click', event => { if (event.target === dialog) dialog.close(); });
        }
      }
    }
    // Use article product-gallery behavior: whole image opens the enlarged viewer.
    if (rail && !rail.hidden) {
      const trigger = root.querySelector('[data-ab-zoom-open]');
      const frame = root.querySelector('.ab-journal-article__media-frame');
      if (trigger && frame) {
        listen(frame, 'click', e => {
          if (e.target.closest('button, a')) return;
          trigger.click();
        });
      }
    }
    // Related and discovery carousels: native scrolling, keyboard support and
    // inert arrow states. No carousel dependency or external network requests.
    root.querySelectorAll('[data-ab-story-carousel]').forEach(carousel => {
      if (carousel.dataset.abCarouselReady === 'true') return;
      carousel.dataset.abCarouselReady = 'true';
      const track=carousel.querySelector('[data-ab-story-track]');
      const prev=carousel.querySelector('[data-ab-story-prev]');
      const next=carousel.querySelector('[data-ab-story-next]');
      if (!track || !prev || !next) return;
      const update=()=>{
        const max=Math.max(0,track.scrollWidth-track.clientWidth);
        prev.disabled=track.scrollLeft<=2;
        next.disabled=track.scrollLeft>=max-2;
        carousel.classList.toggle('ab-journal-article__rail--single',max<=2);
      };
      const move=direction=>{
        const card=track.querySelector('.ab-journal-article__story-card, .ab-journal-article__product-card');
        if (!card) return;
        const gap=parseFloat(window.getComputedStyle(track).columnGap)||0;
        const cardStep=card.getBoundingClientRect().width+gap;
        const page=Math.max(1,Math.floor((track.clientWidth+gap)/cardStep));
        const reduced=window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        track.scrollBy({left:direction*cardStep*page,behavior:reduced?'auto':'smooth'});
      };
      listen(prev, 'click', ()=>move(-1));
      listen(next, 'click', ()=>move(1));
      listen(track, 'scroll', update, {passive:true});
      listen(window, 'resize', update, {passive:true});
      update();
    });

    const copy = root.querySelector('[data-ab-copy-link]');
    if (copy) listen(copy, 'click', async () => {
      const status = root.querySelector('[data-ab-copy-status]');
      try {
        await navigator.clipboard.writeText(copy.dataset.url);
        if (status) status.textContent = 'Link copied';
      } catch (_) {
        if (status) status.textContent = 'Copy from your address bar';
      }
    });
  }
  function initializeAll() {
    activeRoots.forEach(root => {
      if (!root.isConnected) root.__abJournalArticleCleanup?.();
    });
    document.querySelectorAll('[data-ab-journal-article]').forEach(init);
  }
  function cleanupSection(event) {
    const scope = event.target;
    if (!scope) return;
    if (scope.matches?.('[data-ab-journal-article]')) scope.__abJournalArticleCleanup?.();
    scope.querySelectorAll?.('[data-ab-journal-article]').forEach(root => root.__abJournalArticleCleanup?.());
  }
  window.__abJournalArticleRuntime = { initializeAll };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initializeAll);
  else initializeAll();
  document.addEventListener('shopify:section:load', initializeAll);
  document.addEventListener('shopify:section:unload', cleanupSection);
  let ticking = false;
  function updateProgress() {
    ticking = false;
    document.querySelectorAll('[data-ab-journal-article]').forEach(root => {
      const region = root.querySelector('[data-ab-reading-region]');
      const bar = root.querySelector('[data-ab-reading-progress]');
      if (!region || !bar) return;
      const top = region.getBoundingClientRect().top + window.scrollY;
      const height = region.offsetHeight;
      const ratio = Math.max(0, Math.min(1, (window.scrollY - top) / Math.max(height - window.innerHeight * 0.3, 1)));
      bar.style.transform = 'scaleX(' + ratio + ')';
    });
  }
  window.addEventListener('scroll', () => {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(updateProgress);
  }, { passive: true });
  window.addEventListener('resize', updateProgress, { passive: true });
  document.addEventListener('DOMContentLoaded', updateProgress);
})();